import { DayjsProperty } from '@/lib/jackson'
import type { LoyaltyToastBlock } from '@/services/loyalty'
import { formatValueStringToLabel } from '@/utils/helper'
import type { Dayjs } from 'dayjs'
import dayjs from 'dayjs'
import { JsonProperty, Serializable } from 'ts-jackson'
import { Fromable } from '../model'

@Serializable()
export class CheckInData extends Fromable {
  @JsonProperty('id')
  checkInId!: number

  @JsonProperty('memberId')
  memberId!: number

  @JsonProperty('code')
  code!: string

  @JsonProperty('memberName')
  memberName!: string

  @DayjsProperty()
  checkInDateTime!: Dayjs

  @DayjsProperty()
  memberExpireDateTime!: Dayjs | null

  @JsonProperty({ path: 'checkOutDateTime', required: false })
  checkOutDateTime?: string | null

  @JsonProperty({ path: 'durationSeconds', required: false })
  durationSeconds?: number | null

  /** MEMBER | SESSION_MEMBER | DROP_IN — absent on old BEs. */
  @JsonProperty({ path: 'memberType', required: false })
  memberType?: string | null

  /** Names of currently-active packages — absent on old BEs. */
  @JsonProperty({ path: 'activePackages', required: false })
  activePackages?: string[] | null

  get DataForTable() {
    const expireDate = this.memberExpireDateTime?.local() ?? null
    const diffDays = expireDate
      ? expireDate.startOf('day').diff(dayjs().local().startOf('day'), 'day') + 1
      : null
    return {
      id: this.memberId,
      checkInId: this.checkInId,
      code: this.code,
      name: this.memberName,
      checkInDate: this.checkInDateTime.format('DD MMMM YYYY'),
      checkInTime: this.checkInDateTime.format('HH:mm:ss'),
      checkOutDateTime: this.checkOutDateTime ?? null,
      durationSeconds: this.durationSeconds ?? null,
      memberType: this.memberType ? formatValueStringToLabel(this.memberType) : null,
      activePackages: this.activePackages ?? [],
      // Raw expire fields — separate so renderers can color the relative
      // sub-line (warn ≤30 / danger ≤7 or expired) without re-parsing.
      memberExpireDate: expireDate?.format('DD/MM/YYYY') ?? null,
      // Two aliases for the relative days: `memberExpireDiffDays` (loyalty
      // renderer) and `memberExpireDaysLeft` (develop renderer) — keep both.
      memberExpireDiffDays: diffDays,
      memberExpireDaysLeft: diffDays,
      // Backward-compatible string version used by the legacy SimpleTable.
      memberExpireDateTime:
        expireDate && diffDays != null
          ? `${expireDate.format('DD/MM/YYYY')} (In ${diffDays} Days)`
          : null,
    }
  }
}

/**
 * QTY-2048: why a check-in charged nothing. Null when it did charge.
 *
 * The `string` arm is deliberate — it keeps the known value in autocomplete
 * while admitting that the backend can send a reason this client has no
 * wording for yet, so consumers must handle the unknown case.
 */
export type CheckInDeductionReason = 'ALREADY_CHECKED_IN_TODAY' | (string & {})

/**
 * QTY-2048: what a check-in cost in package uses, returned on the same call
 * so the front desk sees the outcome on one click — there is no confirm step.
 *
 * Absent when the member holds no time-based package (an unlimited membership,
 * say) and on backends predating the feature, which is why every consumer
 * treats `undefined` as "show nothing".
 */
export type CheckInDeduction = {
  deducted: boolean
  reason: CheckInDeductionReason | null
  membershipId: number | null
  packageName: string | null
  /** -1 when charged, 0 for a free same-day repeat. */
  usesDelta: number | null
  remainingUses: number | null
}

/**
 * Raw response of POST /member/checkin (and the QR variant). `loyalty` is the
 * BE's LoyaltyToastBlock — present only when the loyalty gate is on; absent on
 * old BEs (QTY-1713). Plain type (no ts-jackson) because the caller consumes
 * the wire shape directly.
 */
export type CheckInMemberResponse = {
  memberId: number
  memberName: string
  /** QTY-2049: needed to undo this check-in's charge. */
  checkInId?: number
  checkInDateTime: string
  memberExpireDateTime: string
  deduction?: CheckInDeduction
  loyalty?: LoyaltyToastBlock
}

/** QTY-2049: result of reversing a check-in's charge. */
export type CheckInUndoResult = {
  checkInId: number
  membershipId: number
  remainingUses: number
}

export type CheckOutData = {
  memberId: number
  memberName: string
  checkInDateTime: string
  checkOutDateTime: string
  durationSeconds: number
}

export type PeakHourRow = {
  checkin_date: string
  weekday: string
  weekday_index: number
  period: string
  total_checkins: string
}

export type PeakHoursResponse = {
  data: PeakHourRow[]
}
